import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { employerAPI } from '../../services/api';
import { 
  Shield, CheckCircle, Circle, Loader2, ChevronDown, ChevronUp,
  Building2, BadgeCheck, Award, Crown, Sparkles
} from 'lucide-react';

// Company tier system
const COMPANY_TIER_ICONS = {
  NONE: Circle,
  BASIC: Shield,
  VERIFIED_EMPLOYER: BadgeCheck,
  TRUSTED_EMPLOYER: Award,
  ELITE_EMPLOYER: Crown
};

// Design guidelines compliant colors for company tiers
const COMPANY_TIER_COLORS = {
  NONE: { bg: 'bg-[#86858D]/12', text: 'text-[#86858D]', border: 'border-[#86858D]/25', solid: 'bg-[#86858D]', hex: '#86858D' },
  BASIC: { bg: 'bg-[#86858D]/12', text: 'text-[#86858D]', border: 'border-[#86858D]/25', solid: 'bg-[#86858D]', hex: '#86858D' },
  VERIFIED_EMPLOYER: { bg: 'bg-[#5B8AF0]/12', text: 'text-[#5B8AF0]', border: 'border-[#5B8AF0]/25', solid: 'bg-[#5B8AF0]', hex: '#5B8AF0' },
  TRUSTED_EMPLOYER: { bg: 'bg-[#E8505A]/12', text: 'text-[#E8505A]', border: 'border-[#E8505A]/25', solid: 'bg-[#E8505A]', hex: '#E8505A' },
  ELITE_EMPLOYER: { bg: 'bg-[#F0A030]/12', text: 'text-[#F0A030]', border: 'border-[#F0A030]/25', solid: 'bg-[#F0A030]', hex: '#F0A030' }
};

const COMPANY_TIER_ORDER = ['NONE', 'BASIC', 'VERIFIED_EMPLOYER', 'TRUSTED_EMPLOYER', 'ELITE_EMPLOYER'];

const CompanyTierCard = ({ compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [tierData, setTierData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchTier();
  }, []);

  const fetchTier = async () => {
    try {
      const res = await employerAPI.getCompanyTier(); 
      setTierData(res.data);
    } catch (error) {
      console.error('Error fetching company tier:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#1A1A2E] border border-[#2A2A4A] rounded-xl p-4 animate-pulse">
        <div className="h-20 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-[#8888AA]" />
        </div>
      </Card>
    );
  }

  if (!tierData) return null;

  const { 
    tier = 'NONE', 
    tier_name = 'None', 
    criteria_met = [], 
    criteria_needed = [], 
    next_tier,
    next_tier_name,
    next_tier_requirements = [],
    metrics = {}
  } = tierData;
  
  const TierIcon = COMPANY_TIER_ICONS[tier] || Circle;
  const colors = COMPANY_TIER_COLORS[tier] || COMPANY_TIER_COLORS.NONE;
  const tierIndex = COMPANY_TIER_ORDER.indexOf(tier);
  const isMaxTier = tier === 'ELITE_EMPLOYER';

  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg} ${colors.border} border`}
        data-testid="company-tier-badge-compact"
      >
        <TierIcon className={`w-4 h-4 ${colors.text}`} />
        <span className={`text-xs font-medium ${colors.text}`}>{tier_name}</span>
      </div>
    );
  }

  return (
    <Card 
      className="bg-[#1A1A2E] border border-[#2A2A4A] rounded-xl p-5 relative overflow-hidden"
      data-testid="company-tier-card"
    >
      {/* Decorative gradient */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${colors.solid}`} />
      
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${colors.bg} ${colors.border} border`}>
            <TierIcon className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">Company Tier</h3>
            <p className={`text-sm font-medium ${colors.text}`}>{tier_name}</p>
          </div>
        </div>
        {!isMaxTier && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="text-[#8888AA] hover:text-white"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        )}
      </div>

      {/* Tier Progress */}
      <div className="mb-4">
        <div className="flex items-center gap-1 mb-2">
          {COMPANY_TIER_ORDER.slice(1).map((t, i) => {
            const isActive = i < tierIndex;
            const isCurrent = i === tierIndex - 1;
            const tierColors = COMPANY_TIER_COLORS[t];
            
            return (
              <div 
                key={t}
                className={`flex-1 h-1.5 rounded-full transition-all ${
                  isActive || isCurrent ? tierColors.solid : 'bg-[#2A2A4A]'
                }`}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-[#6A6A8A] uppercase tracking-wide">
          <span>Basic</span>
          <span>Verified</span>
          <span>Trusted</span>
          <span>Elite</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-[#12121F] rounded-lg">
        <div className="text-center">
          <p className="text-lg font-bold text-white">{metrics.total_records || 0}</p>
          <p className="text-[10px] text-[#8888AA] uppercase">Records</p>
        </div>
        <div className="text-center border-x border-[#2A2A4A]">
          <p className="text-lg font-bold text-white">{metrics.acknowledgment_rate?.toFixed(0) || 0}%</p>
          <p className="text-[10px] text-[#8888AA] uppercase">Ack Rate</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-white">{metrics.months_active?.toFixed(0) || 0}</p>
          <p className="text-[10px] text-[#8888AA] uppercase">Months</p>
        </div>
      </div>

      {/* Next Tier Progress (only show if not at max tier) */}
      {!isMaxTier && next_tier && (
        <div className="p-3 bg-[#12121F] rounded-lg mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#8888AA]">Next: {next_tier_name}</span>
            <Sparkles className={`w-3 h-3 ${COMPANY_TIER_COLORS[next_tier]?.text || 'text-gray-400'}`} />
          </div>
          {next_tier_requirements.length > 0 && (
            <ul className="space-y-1">
              {next_tier_requirements.slice(0, 3).map((req, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#8888AA]">
                  <Circle className="w-2 h-2 mt-1 flex-shrink-0" />
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Expanded Details */}
      {showDetails && (
        <div className="border-t border-[#2A2A4A] pt-4 space-y-4">
          {/* Criteria Met */}
          {criteria_met.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[#8888AA] uppercase tracking-wide mb-2">
                Achieved
              </h4>
              <ul className="space-y-1.5">
                {criteria_met.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-green-400">
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Criteria Needed */}
          {criteria_needed.length > 0 && !isMaxTier && (
            <div>
              <h4 className="text-xs font-semibold text-[#8888AA] uppercase tracking-wide mb-2">
                Still Needed
              </h4>
              <ul className="space-y-1.5">
                {criteria_needed.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#8888AA]">
                    <Circle className="w-3 h-3 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Elite Tier Badge */}
      {isMaxTier && (
        <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-yellow-900/20 to-amber-900/20 rounded-lg border border-yellow-500/20">
          <Crown className="w-4 h-4 text-yellow-400" />
          <span className="text-sm text-yellow-400 font-medium">
            Elite Employer Status Achieved
          </span>
        </div>
      )}
    </Card>
  );
};

// Compact badge component for inline display - Design guidelines compliant
export const CompanyTierBadge = ({ tier, tier_name, size = 'md' }) => {
  const TierIcon = COMPANY_TIER_ICONS[tier] || Circle;
  const colors = COMPANY_TIER_COLORS[tier] || COMPANY_TIER_COLORS.NONE;
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-[8px] tracking-[0.12em]',
    md: 'px-3 py-1.5 text-[9px] tracking-[0.12em]',
    lg: 'px-4 py-2 text-[10px] tracking-[0.12em]'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  return (
    <div 
      className={`inline-flex items-center gap-1.5 rounded-full ${colors.bg} ${colors.border} border ${sizeClasses[size]} font-mono uppercase`}
      data-testid="company-tier-badge"
    >
      <TierIcon className={`${iconSizes[size]} ${colors.text}`} />
      <span className={`font-medium ${colors.text}`}>{tier_name || 'Unknown'}</span>
    </div>
  );
};

export default CompanyTierCard;
